import { skipToken } from '@apollo/client/react'
import { useTimeout } from '@mantine/hooks'
import type { MotionValue } from 'motion/react'
import { AnimatePresence, motion, useTime, useTransform } from 'motion/react'
import type { PropsWithChildren } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncFn } from 'react-use'
import IcBaselineClose from '~icons/ic/baseline-close'
import MdiRefresh from '~icons/mdi/refresh'
import { openTaskPublishDialog } from 'src/components/GenerationTask/Common/TaskPublishDialog'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import Text from 'src/components/ui/Text'
import { useTaskById } from 'src/services/apollo/task'
import { acquirePreGeneratedTask } from 'src/services/orpc/task'
import { getAspectRatio, getImagePublicUrl } from 'src/utils/media'
import { TaskStatus } from 'src/utils/task'
import { ONBORADING_ACQUIRE_TASK_SCENE_ID } from '../helper'
import { ImageWithLoadAnimation } from '../ImageWithLoadAnimation'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'
import { NewUserOnboardingFormStep } from '../NewUserOnboardingFormStep'

const PROMPT_ANIMATION_LENGTH_MS = 3000

function GenerationTag(props: {
  tag: string
  time: MotionValue<number>
  index: number
  count: number
}) {
  const { tag, time, count, index } = props
  const startTime = (PROMPT_ANIMATION_LENGTH_MS / count) * index
  const endTime = (PROMPT_ANIMATION_LENGTH_MS / count) * (index + 1)
  const opacity = useTransform(time, [startTime, endTime], [0, 1])
  return (
    <motion.div
      style={{ opacity }}
      className="px-3 py-1 rounded-full bg-neutral-200 dark:bg-neutral-600 text-sm"
    >
      {tag}
    </motion.div>
  )
}

function GenerationTags(props: { prompts: string }) {
  const { prompts } = props
  const tags = useMemo(
    () =>
      prompts
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag),
    [prompts],
  )
  const time = useTime()
  return (
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      {tags.map((tag, index) => (
        <GenerationTag
          key={tag}
          tag={tag}
          time={time}
          index={index}
          count={tags.length}
        />
      ))}
    </div>
  )
}

function GenerationState(props: PropsWithChildren) {
  return (
    <motion.div
      className={'absolute left-0 top-0 w-full h-full p-6 overflow-auto'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {props.children}
    </motion.div>
  )
}

function FollowUsButton() {
  const { socialMediaDataRes } = useContext(NewUserOnboardingContext)
  const socialMedia = socialMediaDataRes?.find(
    item => item.name === 'X' || item.name === 'Naver Cafe',
  )
  const { i18n } = useTranslation(['common', 'user'])
  return socialMedia ? (
    <UILink
      href={socialMedia.link}
      target="_blank"
      variant="ghost"
      className="px-2.5 h-8 no-underline grid"
    >
      <Text
        i18n={i18n}
        i18nKey="user:onboarding.generation.follow-us.label"
        values={{
          amount: (25000).toLocaleString(),
          nextAmount: (10000).toLocaleString(),
        }}
        components={{ icon: <socialMedia.icon className="inline size-6" /> }}
      />
    </UILink>
  ) : null
}

export function NewUserGenerationTaskStep() {
  const { t } = useTranslation(['common', 'user'])
  const { goToNextStepWithAction } = useContext(NewUserOnboardingContext)
  const form = useNewUserOnboardingFormContext()
  const [fakeLoading, setFakeLoading] = useState(true)
  const { start: startFakeLoad } = useTimeout(() => setFakeLoading(false), 5000)
  const [
    {
      loading: loadingAcquiredTask,
      value: acquiredTask,
      error: acquireTaskError,
    },
    acquireTask,
  ] = useAsyncFn(async () => {
    const tackId = form.getValues().characters?.at(0)
    const task = await acquirePreGeneratedTask({
      sceneId: ONBORADING_ACQUIRE_TASK_SCENE_ID,
      tackId,
    })
    startFakeLoad()
    return task
  }, [form.getValues, startFakeLoad])
  // orpc acquire only returns base fields; parameters/mediaId come from this fetch.
  const { data: updatedTask } = useTaskById(acquiredTask?.id ?? skipToken)

  useEffect(() => {
    acquireTask()
  }, [])
  const status = updatedTask?.status ?? acquiredTask?.status
  const prompts = updatedTask?.parameters?.prompts ?? ''
  const failed =
    acquireTaskError != null ||
    status === TaskStatus.failed ||
    status === TaskStatus.cancelled
  // Pre-generated tasks usually come back as completed immediately, but the
  // "done" view needs media/prompts from the follow-up GraphQL fetch — gate
  // ready state on updatedTask so the preview never flashes empty.
  const ready = updatedTask?.status === TaskStatus.completed
  const loading = loadingAcquiredTask || ((fakeLoading || !ready) && !failed)

  const retry = useCallback(() => {
    setFakeLoading(true)
    acquireTask()
  }, [acquireTask])

  return (
    <>
      <NewUserOnboardingFormStep
        disableNext={loading}
        disallowBack
        overridePaginatorContent={
          <>
            <UIButton
              disabled={loading || failed}
              className="flex-1"
              color="theme"
              variant="filled"
              onPress={() => {
                goToNextStepWithAction()
                openTaskPublishDialog({
                  input: updatedTask ?? acquiredTask?.id,
                  mediaIdx: 0,
                  scene: 'new-user-onboarding',
                })
              }}
            >
              {t('user:onboarding.generation.publish.label')}
            </UIButton>
          </>
        }
        overrideStepperContent={
          <>
            <div className="flex-1"></div>
            {loading ? null : (
              <UIButton
                variant="ghost"
                className="right-6 top-6 h-7 w-7 mr-3 text-neutral-500 text-sm font-normal"
                labelClassName="flex justify-center items-center"
                onPress={() => goToNextStepWithAction()}
              >
                <IcBaselineClose />
              </UIButton>
            )}
          </>
        }
      >
        <AnimatePresence initial={false} mode="wait">
          {loading ? (
            <GenerationState key="loading">
              <h2 className="text-center text-xl font-bold mb-4">
                {t('user:onboarding.generation.title.loading')}
              </h2>
              <p className="text-center text-neutral-600 dark:text-neutral-400">
                {t('user:onboarding.generation.info.loading')}
              </p>
              {loadingAcquiredTask ? null : (
                <GenerationTags prompts={prompts} />
              )}
            </GenerationState>
          ) : failed ? (
            <GenerationState key="failed">
              <h2 className="text-center text-xl font-bold mb-4">
                {t('user:onboarding.generation.title.failed')}
              </h2>
              <p className="flex flex-col items-center justify-center h-80">
                <UIButton
                  variant="ghost"
                  className="px-2.5 h-8"
                  icon={<MdiRefresh />}
                  onPress={retry}
                >
                  {t('user:onboarding.generation.retry.label')}
                </UIButton>
              </p>
            </GenerationState>
          ) : (
            <GenerationState key="done">
              <h2 className="text-center text-xl font-bold mb-4">
                {t('user:onboarding.generation.title.ready')}
              </h2>
              <p className="text-center text-neutral-600 dark:text-neutral-400">
                {t('user:onboarding.generation.info.ready')}
              </p>
              <div className="mt-4 flex flex-col items-center">
                <ImageWithLoadAnimation
                  wrapperClassName="h-[316px] rounded-lg"
                  wrapperStyle={{
                    aspectRatio: getAspectRatio(updatedTask?.media),
                  }}
                  src={getImagePublicUrl(
                    updatedTask?.mediaId,
                    updatedTask?.media,
                  )}
                  alt={prompts}
                />
              </div>
              <div className="mt-4 flex flex-col items-center">
                <FollowUsButton />
              </div>
            </GenerationState>
          )}
        </AnimatePresence>
      </NewUserOnboardingFormStep>
    </>
  )
}
